import { ref, computed, nextTick, SetupContext, reactive } from "vue";
import { UiModalEmits, UiModalProps } from "../index";
import { node, utility } from "@various/utils";

export const useComposable = (define: UiModalProps, emits: SetupContext<typeof UiModalEmits>["emit"]) => {
    //* 静态变量
    const vars = {
        spacing: { x: "", y: "" }, //* 内间距属性
        overflow: "", //* overflow属性
    };

    //* 响应式变量
    const refs = {
        open: ref<boolean>(false),
        main: ref<HTMLElement>(),
        observer: ref<ResizeObserver>(),
        container: ref<HTMLElement>(),
    };

    //* 计算属性
    const computeds = {
        //* 主体样式
        mainStyle: computed(() => {
            return {
                padding: define.margin,
                zIndex: define.zIndex,
            };
        }),
        //* 容器样式
        containerStyle: computed(() => {
            //* 初始化返回数据
            const result: { [name: string]: any } = {
                width: utility.isNumber(define.width) ? define.width + "px" : define.width,
                height: utility.isNumber(define.height) ? define.height + "px" : define.height,
                padding: define.spacing,
            };

            if (define.minHeight) result["min-height"] = utility.isNumber(define.minHeight) ? define.minHeight + "px" : define.minHeight;
            if (define.maxHeight) result["max-height"] = utility.isNumber(define.maxHeight) ? define.maxHeight + "px" : define.maxHeight;
            if (define.minWidth) result["min-width"] = utility.isNumber(define.minWidth) ? define.minWidth + "px" : define.minWidth;
            if (define.maxWidth) {
                result["max-width"] = utility.isNumber(define.maxWidth) ? define.maxWidth + "px" : define.maxWidth;
            }

            return result;
        }),
    };

    //* 函数列表
    const methods = {
        //* 窗口初始化函数
        resizeHandler: () => {
            nextTick(() => {
                if (!refs.container.value || !refs.main.value) return;
                if (refs.container.value.offsetHeight >= window.innerHeight) {
                    refs.main.value.style.alignItems = "flex-start";
                } else {
                    refs.main.value.style.alignItems = "center";
                }
            });
        },

        //* Modal关闭函数
        closeModal: () => {
            //* 检测是否满足运行条件
            if (!refs.main.value || !refs.observer.value) return;
            else {
                //* 卸载监听事件
                refs.observer.value.disconnect();
                //* 隐藏弹出窗口
                refs.open.value = false;
                //* 响应关闭事件
                emits("close");
            }
        },

        //* Modal启动函数
        openModal: () => {
            //* 检测是否满足运行条件
            if (!refs.main.value || !refs.observer.value) return;
            else {
                //* 隐藏的滚动条
                if (!refs.open.value) {
                    //* 数据缓存
                    vars.spacing = { x: document.body.style.paddingRight, y: document.body.style.paddingBottom };
                    vars.overflow = document.body.style.overflow;
                    //* 隐藏滚动条
                    node.scrollbarHidden();
                }

                //* 回到顶部
                refs.main.value?.scrollTo({ top: 0 });
                //* 挂载监听事件
                refs.observer.value.observe(refs.main.value);
                //* 显示弹出窗口
                refs.open.value = true;
                //* 响应Open事件
                emits("open");
            }
        },

        //* Modal滚动条函数
        scrollTo: (options: ScrollToOptions) => {
            refs.main.value?.scrollTo(options);
        },
    };

    //* 属性
    const binds = reactive({
        main: computed(() => {
            return {
                style: computeds.mainStyle.value,
                class: define.classExtraName,
            };
        }),
    });

    //* 事件列表
    const ons = {
        animation: {
            "after-leave": () => {
                //* 恢复滚动条
                document.body.style.overflow = vars.overflow;
                document.body.style.paddingRight = vars.spacing.x;
                document.body.style.paddingBottom = vars.spacing.y;
                //* 事件响应
                emits("after-leave");
            },
        },
    };

    return { ons, vars, refs, binds, methods, computeds };
};
